import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'motion/react';
import { useUiStore } from '@/store/useUiStore';
import Reveal from '@/components/Reveal';
import './Ch6Partners.css';
import './Ch7Methodology.css';

interface Stage {
  title: string;
  en: string;
  build: string;
}
interface Gov {
  num: string;
  title: string;
  body: string;
}

const LOCAL_LOGOS = ['/partners/alchemy-global.png', '/partners/al-rajhi.png', '/partners/jawraa.png'];
const GLOBAL_LOGOS = [
  '/partners/dynatrace.png',
  '/partners/lusinova.png',
  '/partners/google.png',
  '/partners/microsoft.png',
  '/partners/nvidia.png',
  '/partners/dell.png',
];

function PartnerRow({ label, names, logos }: { label: string; names: string[]; logos: string[] }) {
  const reduce = useReducedMotion();
  return (
    <div className="partners-row">
      <span className="partners-row-label">{label}</span>
      <div className="partners-logos">
        {logos.map((src, i) => (
          <motion.div
            key={src}
            className="partner-chip"
            animate={reduce ? undefined : { y: [0, -6, 0] }}
            transition={
              reduce ? undefined : { duration: 4 + (i % 3), repeat: Infinity, ease: 'easeInOut', delay: i * 0.2 }
            }
          >
            <img src={src} alt={names[i] ?? ''} loading="lazy" />
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/**
 * CH — Ecosystem (منظومتنا, PDF chapter 5): combines Partners + the value-creation
 * methodology + governance into one chapter.
 */
export default function Ch7Ecosystem() {
  const { t } = useTranslation();
  const setActiveChapter = useUiStore((s) => s.setActiveChapter);
  const local = t('partners.local', { returnObjects: true }) as string[];
  const global = t('partners.global', { returnObjects: true }) as string[];
  const stages = t('methodology.stages', { returnObjects: true }) as Stage[];
  const gov = t('methodology.governance', { returnObjects: true }) as Gov[];

  return (
    <section
      id="ecosystem"
      className="chapter chapter--tint chapter-ecosystem"
      aria-labelledby="ecosystem-title"
      onMouseEnter={() => setActiveChapter('ecosystem')}
    >
      <div className="chapter-inner">
        <div className="chapter-head chapter-head--center">
          <p className="section-kicker">{t('ecosystem.kicker')}</p>
          <h2 id="ecosystem-title" className="chapter-title">
            {t('ecosystem.title')}
          </h2>
          <p className="chapter-lead">{t('ecosystem.lead')}</p>
        </div>

        {/* Partners */}
        <div className="eco-block">
          <h3 className="eco-subtitle">{t('partners.title')}</h3>
          <Reveal>
            <PartnerRow label={t('partners.localLabel')} names={local} logos={LOCAL_LOGOS} />
          </Reveal>
          <Reveal delay={0.1}>
            <PartnerRow label={t('partners.globalLabel')} names={global} logos={GLOBAL_LOGOS} />
          </Reveal>
        </div>

        {/* Value-creation methodology */}
        <div className="eco-block">
          <h3 className="eco-subtitle">{t('methodology.journeyTitle')}</h3>
          <div className="method-journey" role="list">
            <div className="method-track">
              {stages.map((s, i) => (
                <div className="method-stage" role="listitem" key={s.en}>
                  <div className="method-node">
                    <span className="method-node-num">{String(i + 1).padStart(2, '0')}</span>
                  </div>
                  <h4 className="method-stage-title">{s.title}</h4>
                  <span className="method-stage-en">{s.en}</span>
                  <p className="method-stage-build">{s.build}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Governance */}
        <div className="eco-block">
          <h3 className="eco-subtitle">{t('methodology.governanceTitle')}</h3>
          <div className="gov-grid">
            {gov.map((g, i) => (
              <Reveal key={g.num} delay={i * 0.08}>
                <article className="gov-hex-card">
                  <span className="gov-hex" aria-hidden="true">
                    <span className="gov-hex-num">{g.num}</span>
                  </span>
                  <div className="gov-hex-body">
                    <h4 className="gov-hex-title">{g.title}</h4>
                    <p className="gov-hex-text">{g.body}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
